import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
}

class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('🚨 ErrorBoundary caught an error:', error, errorInfo);
    this.setState({
      error,
      errorInfo,
    });
  }

  private handleReload = () => {
    window.location.reload();
  };

  public render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
          <Card className="max-w-2xl w-full">
            <CardHeader>
              <CardTitle className="text-red-600">🚨 Erreur de l'application</CardTitle>
              <CardDescription>
                Une erreur inattendue s'est produite. Voici les détails :
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {this.state.error && (
                <div className="bg-red-50 p-4 rounded-md">
                  <h3 className="font-semibold text-red-800 mb-2">Erreur :</h3>
                  <pre className="text-sm text-red-700 whitespace-pre-wrap">
                    {this.state.error.message}
                  </pre>
                </div>
              )}

              {this.state.errorInfo && (
                <div className="bg-gray-50 p-4 rounded-md">
                  <h3 className="font-semibold text-gray-800 mb-2">Stack Trace :</h3>
                  <pre className="text-xs text-gray-600 whitespace-pre-wrap overflow-auto max-h-64">
                    {this.state.errorInfo.componentStack}
                  </pre>
                </div>
              )}

              <div className="flex gap-2 pt-4">
                <Button onClick={this.handleReload}>Recharger la page</Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    this.setState({ hasError: false, error: undefined, errorInfo: undefined });
                  }}
                >
                  Réessayer
                </Button>
              </div>

              <div className="text-sm text-gray-500 mt-4">
                <p>
                  💡 <strong>Conseils de débogage :</strong>
                </p>
                <ul className="list-disc list-inside mt-2 space-y-1">
                  <li>Vérifiez la console du navigateur (F12) pour plus de détails</li>
                  <li>
                    Vérifiez que Supabase est démarré : <code>supabase status</code>
                  </li>
                  <li>
                    Nettoyez le cache : <code>rm -rf node_modules/.vite</code>
                  </li>
                </ul>
              </div>
            </CardContent>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
